import {
  dataResponseConverter,
  Endpoint,
  MakeRequest,
  RestResponse,
  SendFile,
} from './request'
import {
  AuthType,
  Gender,
  OwnerModel,
  ScheduleDayModel,
  SettingsOperation,
  SubscriptionOperationType,
  UploadImageResponse,
  UserModel,
  UserStatsModel,
} from '../models'
import type {AppStorage} from '../index'
import type {Secure} from '../helpers/userSecure'
import {mapRestUsersToAppUsers} from './mappers'
import type {AuthExistsResponse} from '../models/auth'
import {
  ChangeContactRequestResolution,
  ChangeContactResponse,
  convertIncomingToRequestParams,
  convertVerifyModeToRequestParam,
  Credentials,
  Incoming,
  VerifyMode,
} from '../models/auth'

export class UserApi {
  constructor(
    private request: MakeRequest,
    private storage: AppStorage,
    private secure: Secure,
  ) {}

  userInfo = (id: string): Promise<RestResponse<OwnerModel>> => {
    return this.request.newRequest<OwnerModel>({
      endpoint: 'v3/user',
      body: {
        data: {userHash: id},
      },
    })
  }

  userStats = (): Promise<RestResponse<UserStatsModel>> => {
    return this.request.newRequest<UserStatsModel>(
      {
        endpoint: 'v2/user/profile-stat',
      },
      dataResponseConverter,
    )
  }

  checkExisting = (data: {
    phone?: string
    email?: string
  }): Promise<RestResponse<AuthExistsResponse>> => {
    return this.request.newRequest<AuthExistsResponse>({
      endpoint: 'v2/user/exist',
      body: {
        data: {number: data.phone, email: data.email},
      },
    })
  }

  register = (
    credentials: Credentials,
    params: {
      name: string
      surname: string
      imageId?: string
      recaptchaToken?: string
    },
  ): Promise<RestResponse<undefined>> => {
    const {phone: number} =
      credentials.type === AuthType.phone ? credentials : {phone: undefined}
    const {email, password} =
      credentials.type === AuthType.email
        ? credentials
        : {email: undefined, password: undefined}
    return this.request.newRequest<undefined>(
      {
        endpoint: 'v2/user/register',
        body: {
          data: {
            number,
            email,
            password,
            firstName: params.name,
            lastName: params.surname,
            imageHash: params.imageId,
            captcha: params.recaptchaToken,
          },
        },
        allowEmptyResponse: true,
      },
      dataResponseConverter,
    )
  }

  changeContact = (
    verifyMode: VerifyMode,
    code: string,
    incoming: Incoming,
    conflictsResolving?: {
      manualResolving: boolean
      resolution?: ChangeContactRequestResolution[]
    },
  ): Promise<RestResponse<ChangeContactResponse>> => {
    const type = convertVerifyModeToRequestParam(verifyMode)
    return this.request.newRequest<ChangeContactResponse>({
      endpoint: 'v2/user/change-contact',
      body: {
        ...convertIncomingToRequestParams(incoming),
        type,
        code,
        manualResolving: conflictsResolving?.manualResolving,
        resolution: conflictsResolving?.resolution,
      },
      allowEmptyResponse: true,
    })
  }

  changeUserAddress = (params: {
    addressFrom: 'coordinates'
    address: string | undefined
    latitude: number
    longitude: number
  }): Promise<RestResponse<any>> => {
    return this.request.newRequest(
      {
        endpoint: 'v2/user/change-address',
        data: params,
      },
      dataResponseConverter,
    )
  }

  changePersonalData = (params: {
    name: string
    surname: string
    gender: Gender
    avaHash?: string
  }): Promise<RestResponse<any>> => {
    const sex: string = params.gender.toString()
    return this.request.newRequest(
      {
        endpoint: 'v2/user/change-personal',
        data: {
          name: params.name,
          surname: params.surname,
          avaHash: params.avaHash,
          sex,
        },
      },
      dataResponseConverter,
    )
  }

  uploadAvatar = (file: {
    name: string
    type: string
    uri: string
  }): Promise<RestResponse<UploadImageResponse>> => {
    const formData = new FormData()
    // @ts-ignore
    formData.append('image', file)
    return this.request.newRequest<UploadImageResponse>({
      endpoint: 'files/image-user/upload',
      body: formData,
    })
  }

  deleteAccount = (reason: string): Promise<RestResponse<undefined>> => {
    return this.request.newRequest<undefined>({
      endpoint: 'v2/user/delete',
      body: {reason},
      allowEmptyResponse: true,
    })
  }

  changeCallsSchedule = async (
    list: Array<ScheduleDayModel>,
  ): Promise<
    RestResponse<{
      readonly result: string
      readonly status: number
    }>
  > =>
    this.request.newRequest<any>({
      endpoint: `v2/user/change-calls`,
      body: {form: list},
    })

  toggleCalling = async (
    isCallsEnabled: boolean,
  ): Promise<
    RestResponse<{
      readonly result: string
      readonly status: number
    }>
  > =>
    this.request.newRequest<any>({
      endpoint: `v2/user/change-calls`,
      body: {enabled_calls: isCallsEnabled ? 1 : 0},
    })

  // region legacy

  /** @deprecated Use v2 api */
  restUpdateSettings = (
    operation: SettingsOperation,
    params: any,
    file?: SendFile,
  ): Promise<RestResponse<any>> => {
    const hash = this.storage.userHash!
    const secure = this.secure.createUserSecure()

    const sendParams: any = {
      hash,
      secur: secure,
      app_id: this.storage.platform,
      oper: operation,
      form: params,
    }

    return this.request.request<any>({
      api: Endpoint.editUserSettings,
      body: sendParams,
      multipart: !!file,
      file: file,
    })
  }

  /** @deprecated Use v2 api */
  restUpdateWithPhone = (
    isEnabled: boolean,
  ): Promise<RestResponse<boolean>> => {
    return this.restUpdateSettings(SettingsOperation.settingsCalls, {
      enabled_calls: isEnabled,
      // num: this.storage.phone,
      query: '',
    })
  }

  /** @deprecated Use v2 api */
  restUpdateLanguages = async (main: string, additional: Array<string>) => {
    let operation = SettingsOperation.userLanguages
    const params: any = {
      lang_main: main,
      langs: additional.join(';'),
    }
    await this.restUpdateSettings(operation, params)
  }

  /** @deprecated Use v2 api */
  restUserSubscribersSubscriptions = async (
    hash: string,
    type: SubscriptionOperationType,
    page: number = 1,
  ): Promise<RestResponse<UserModel[]>> => {
    const uid = this.storage.userHash

    const params: any = {
      uid: uid ?? '',
      limit: 100,
      hash: hash,
      oper: type,
      page: page,
    }

    const response = await this.request.request<any[]>({
      api: Endpoint.userSubscriptions,
      body: params,
    })
    if (response.error) return response
    return {result: mapRestUsersToAppUsers(response.result ?? [])}
  }

  /** @deprecated Use v2 api */
  restToggleSubscription = (userId: string, isAlreadySubscribed: boolean) => {
    const hash = this.storage.userHash
    const secure = this.secure.createUserSecure()

    const params: any = {
      hash: hash,
      secur: secure,
      hash_user: userId,
      oper: isAlreadySubscribed ? 2 : 1,
    }

    return this.request.request<any>({
      api: Endpoint.subscribeOnUser,
      body: params,
    })
  }

  /** @deprecated Use v2 api */
  restFetchBlackList = async (): Promise<Array<UserModel>> => {
    const hash = this.storage.userHash
    const secure = this.secure.createUserSecure()

    const params: any = {
      hash: hash,
      secur: secure,
      oper: 11,
      form: {oper: 3},
    }

    const response = await this.request.request<any>({
      api: Endpoint.blackList,
      body: params,
    })
    return mapRestUsersToAppUsers(response.result ?? [])
  }

  /** @deprecated Use v2 api */
  restRemoveFromBlackList = async (userHash: string): Promise<boolean> => {
    const hash = this.storage.userHash
    const secure = this.secure.createUserSecure()

    const params: any = {
      hash: hash,
      secur: secure,
      oper: 11,
      form: {oper: 2, user_hash: userHash},
    }

    const response = await this.request.request<any>({
      api: Endpoint.blackList,
      body: params,
    })
    return response.error === undefined
  }

  /** @deprecated Use v2 api */
  restUpdateDegradation = (degradation: number): Promise<RestResponse<any>> => {
    const hash = this.storage.userHash
    const secure = this.secure.createUserSecure()

    const params: any = {
      hash: hash,
      secur: secure,
      app_id: this.storage.platform,
      oper: 7,
      form: {degradation},
    }

    return this.request.request<any>({
      api: Endpoint.editUserSettings,
      body: params,
    })
  }

  // endregion
}
